import {
  IMetricsData,
  IMetricsDataItem,
  TBattery,
} from "../pages/Dashboard/Dashboard.type";
import {
  GARTNER_BATTERY_PHASES,
  GARTNER_CURRENT_COMPLETED_YEAR,
  GARTNER_PHASE_TO_LEVEL,
  LEGACY_BATTERY_TO_PHASE,
  TGartnerBatteryPhase,
} from "../components/SidebarCard/SidebarCardUI/GartnerCharts/constants";

const EPS = 1e-9;
const WINDOW_YEARS = 5;
const NEAR_ZERO_SHARE = 1e-6;

const NUCLEAR_MARKERS = ["атом", "ядерн"] as const;

export type BatteryLevel = 1 | 2 | 3 | 4 | 5;

export interface YearValue {
  year: number;
  value: number;
}

export interface ComputeBatteryRussiaInput {
  levelWorld: BatteryLevel;
  metricsData?: IMetricsData | null;
  /** Median of share_pat across technologies in the same direction. */
  medianSharePatDirection?: number | null;
  /** Names used for nuclear whitelist (direction / cluster / technology). */
  nameHints?: Array<string | null | undefined>;
}

export const levelToBattery = (level: BatteryLevel): TGartnerBatteryPhase =>
  GARTNER_BATTERY_PHASES[level - 1];

/**
 * CMS may still send Концепт/Прототип/Внедрение.
 * Returns canonical Gartner RU label for UI, or "" if unknown/empty.
 */
export const normalizeBattery = (
  battery?: string | null
): TBattery => {
  if (!battery) return "";
  const legacy = LEGACY_BATTERY_TO_PHASE[battery];
  if (legacy) return legacy;
  if (GARTNER_PHASE_TO_LEVEL[battery]) {
    return levelToBattery(GARTNER_PHASE_TO_LEVEL[battery] as BatteryLevel);
  }
  const level = phaseToLevel(battery);
  return level ? levelToBattery(level) : "";
};

export const batteryToLevel = (battery: TBattery): BatteryLevel | null => {
  if (!battery) return null;
  const normalized = normalizeBattery(battery);
  if (!normalized) return null;
  const level = GARTNER_PHASE_TO_LEVEL[normalized];
  if (level >= 1 && level <= 5) return level as BatteryLevel;
  return null;
};

/** Normalize phase_2025 (RU/EN/legacy) → level 1..5. */
export const phaseToLevel = (phase?: string | null): BatteryLevel | null => {
  if (!phase) return null;
  const trimmed = phase.trim();
  if (!trimmed) return null;

  const direct = GARTNER_PHASE_TO_LEVEL[trimmed];
  if (direct) return direct as BatteryLevel;

  const lower = trimmed.toLowerCase();
  for (const [key, level] of Object.entries(GARTNER_PHASE_TO_LEVEL)) {
    if (key.toLowerCase() === lower) return level as BatteryLevel;
  }

  // Partial match for long legacy RU strings
  if (lower.includes("innovation trigger") || lower.includes("запуск технологического"))
    return 1;
  if (lower.includes("inflated expectations") || lower.includes("пик завышенных"))
    return 2;
  if (
    lower.includes("disillusionment") ||
    lower.includes("коррекция ожиданий") ||
    lower.includes("отрезвление") ||
    lower.includes("разочарован")
  )
    return 3;
  if (lower.includes("enlightenment") || lower.includes("осознанного применения"))
    return 4;
  if (lower.includes("plateau") || lower.includes("плато продуктивности"))
    return 5;

  return null;
};

export const computeBatteryWorld = (phase?: string | null): TBattery => {
  const level = phaseToLevel(phase);
  return level ? levelToBattery(level) : "";
};

export const isNuclearEntity = (
  nameHints?: Array<string | null | undefined>
): boolean => {
  if (!nameHints?.length) return false;
  return nameHints.some((name) => {
    if (!name) return false;
    const lower = name.toLowerCase();
    return NUCLEAR_MARKERS.some((marker) => lower.includes(marker));
  });
};

const sumSeriesInWindow = (
  points: YearValue[] | undefined,
  yearFrom: number,
  yearTo: number
): number => {
  if (!points?.length) return 0;
  return points.reduce((sum, point) => {
    if (point.year >= yearFrom && point.year <= yearTo) {
      return sum + (Number(point.value) || 0);
    }
    return sum;
  }, 0);
};

export const computeShare = (
  item: IMetricsDataItem | undefined,
  yearTo: number = GARTNER_CURRENT_COMPLETED_YEAR,
  windowYears: number = WINDOW_YEARS
): number => {
  const yearFrom = yearTo - windowYears + 1;
  const world = sumSeriesInWindow(item?.world, yearFrom, yearTo);
  const russia = sumSeriesInWindow(item?.russia, yearFrom, yearTo);
  return russia / Math.max(world, EPS);
};

/**
 * Gap relative to direction peer median of patent share.
 *  0 if r >= 0.8
 * -1 if 0.3 <= r < 0.8
 * -2 if r < 0.3
 * -1 if almost no RU series
 * min(gap, -2) if science share > 0 and patent share ≈ 0
 */
export const computeRussiaGap = (
  sharePat: number,
  shareSci: number,
  medianSharePatDirection?: number | null
): number => {
  const hasRussiaSignal = sharePat > NEAR_ZERO_SHARE || shareSci > NEAR_ZERO_SHARE;
  if (!hasRussiaSignal) return -1;

  const median = Math.max(medianSharePatDirection ?? sharePat, EPS);
  const r = sharePat / median;

  let gap = 0;
  if (r >= 0.8) gap = 0;
  else if (r >= 0.3) gap = -1;
  else gap = -2;

  if (shareSci > NEAR_ZERO_SHARE && sharePat <= NEAR_ZERO_SHARE) {
    gap = Math.min(gap, -2);
  }

  return gap;
};

const clampLevel = (value: number): BatteryLevel => {
  const rounded = Math.round(value);
  if (rounded < 1) return 1;
  if (rounded > 5) return 5;
  return rounded as BatteryLevel;
};

export const computeBatteryRussia = (
  input: ComputeBatteryRussiaInput
): TBattery => {
  const {
    levelWorld,
    metricsData,
    medianSharePatDirection,
    nameHints,
  } = input;

  const sharePat = computeShare(metricsData?.patents);
  const shareSci = computeShare(metricsData?.science);
  const gap = computeRussiaGap(sharePat, shareSci, medianSharePatDirection);

  let levelRussia = clampLevel(levelWorld + gap);

  if (!isNuclearEntity(nameHints)) {
    levelRussia = Math.min(levelRussia, levelWorld) as BatteryLevel;
  }

  return levelToBattery(levelRussia);
};

export interface ComputeEntityBatteriesInput {
  phase?: string | null;
  metricsData?: IMetricsData | null;
  medianSharePatDirection?: number | null;
  nameHints?: Array<string | null | undefined>;
}

export interface ComputedBatteries {
  world: TBattery;
  russia: TBattery;
}

/** World from Gartner phase_2025; Russia from metrics gap vs world. */
export const computeEntityBatteries = (
  input: ComputeEntityBatteriesInput
): ComputedBatteries => {
  const world = computeBatteryWorld(input.phase);
  const levelWorld = phaseToLevel(input.phase);
  if (!levelWorld) {
    return {world: "", russia: ""};
  }
  const russia = computeBatteryRussia({
    levelWorld,
    metricsData: input.metricsData,
    medianSharePatDirection: input.medianSharePatDirection,
    nameHints: input.nameHints,
  });
  return {world, russia};
};

/** Median of patent RU/world share across peers (e.g. technologies in a cluster). */
export const medianPatentShare = (
  metricsList: Array<IMetricsData | null | undefined>
): number | null => {
  const shares = metricsList
    .map((metrics) => computeShare(metrics?.patents))
    .filter((share) => share > NEAR_ZERO_SHARE)
    .sort((a, b) => a - b);
  if (!shares.length) return null;
  const mid = Math.floor(shares.length / 2);
  return shares.length % 2 === 1
    ? shares[mid]
    : (shares[mid - 1] + shares[mid]) / 2;
};

/** Sort / filter order: higher = more mature. */
export const BATTERY_MATURITY_ORDER: Record<string, number> = {
  "Плато продуктивности": 5,
  "Фаза осознанного применения": 4,
  "Коррекция ожиданий": 3,
  "Пик завышенных ожиданий": 2,
  "Запуск технологического тренда": 1,
};
